import React, { useState, useEffect } from 'react';
import CoachOverview from '../components/dashboard/CoachOverview';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { adminAPI, profilesAPI, tasksAPI, matchesAPI, affiliateAPI, supervisorAPI, coachKpiAPI, kpiV2API } from '../services/api';
import {
  Users,
  Heart,
  CheckSquare,
  Calendar,
  ArrowRight,
  Sparkles,
  Clock,
  Star,
  Award,
  Gift,
  X,
  AlertCircle,
  Bell,
  Plus,
  BarChart3,
  Send,
  Eye,
  Target,
  ChevronDown,
  MessageSquare,
  ListTodo,
  ShieldCheck,
} from 'lucide-react';
import { isSupervisorUser } from '../components/service/sopShared';
import { PageHeader, StatStrip, Section, EmptyState, Pill, S } from '../components/staff/StaffUI';

// How many open KPIv2 deliverables to show before "显示全部".
const DELIVERABLES_PREVIEW = 5;
// Match status → Pill tone.
const MATCH_TONE = {
  pending: 'warn', approved: 'ok', in_contact: 'ok', succeeded: 'ok', completed: 'ok',
  rejected: 'bad', failed: 'bad', cancelled: 'neutral',
};
import { format } from 'date-fns';
import { formatPacific, zonedToInstant, toPacificWallClock } from '../utils/pacificTime';

const Dashboard = () => {
  const { user, hasRole, hasPermission } = useAuth();
  const { t, language } = useLanguage();
  const isZh = language === 'zh';
  const profileTypeLabel = (slug) => {
    const key = String(slug || '').toLowerCase();
    if (!key) return '';
    const zhMap = { member: '会员', lead: '线索', vip: 'VIP', client: '客户', prospect: '意向客户' };
    return isZh ? (zhMap[key] || String(slug)) : String(slug);
  };
  const formatDateLocalized = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return '';
    return dt.toLocaleDateString(isZh ? 'zh-CN' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };
  const matchStatusLabel = (status) => {
    const key = String(status || 'pending').toLowerCase();
    const zhMap = {
      pending: '待处理', approved: '已批准', in_contact: '接触中', succeeded: '已约见', in_relationship: '已交往',
      rejected: '已拒绝', cancelled: '已取消', failed: '失败', completed: '已完成'
    };
    const enMap = {
      pending: 'Pending', approved: 'Approved', in_contact: 'In contact', succeeded: 'Met', in_relationship: 'Dating',
      rejected: 'Rejected', cancelled: 'Cancelled', failed: 'Failed', completed: 'Completed'
    };
    return (isZh ? zhMap[key] : enMap[key]) || status;
  };
  const navigate = useNavigate();
  
  // Internal matchmaker permissions
  const isInternalMatchmaker = user?.matchmaker_type === 'internal';
  const isExternalMatchmaker = user?.matchmaker_type === 'external';
  const isCompanyRepresentative = user?.is_company_representative === true || user?.is_company_representative === 1;
  const canModify = !isInternalMatchmaker || isCompanyRepresentative;
  const isCoach = user?.role?.toLowerCase() === 'coach';
  const isSupervisorCoach = user?.role?.toLowerCase() === 'supervisor_coach';
  const isManagerRole = user?.role?.toLowerCase() === 'manager';
  const isSupervisor = user?.role?.toLowerCase() === 'supervisor';
  const isLeaderCoach = user?.role?.toLowerCase() === 'leader_coach';
  const isCoachOrSupervisor = isCoach || isSupervisorCoach;
  // Roles that see the KPIv2 "未完成交付项" card on the dashboard.
  const showKpiV2Incomplete = isCoach || isLeaderCoach || isSupervisorCoach || isSupervisor;


  // Coach match permissions
  const canViewMatches = hasPermission('matches.view') || hasPermission('coaches.manage_assigned_matches');
  const canCreateMatches = hasPermission('matches.create');
  const canEditMatches = hasPermission('matches.edit');
  
  const [stats, setStats] = useState(null);
  const [recentProfiles, setRecentProfiles] = useState([]);
  const [todaysTasks, setTodaysTasks] = useState([]);
  const [recentMatches, setRecentMatches] = useState([]);
  const [affiliateStats, setAffiliateStats] = useState(null);
  const [affiliateTiers, setAffiliateTiers] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [incompleteDeliverables, setIncompleteDeliverables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDailyTasksModal, setShowDailyTasksModal] = useState(false);
  const [dailyTasks, setDailyTasks] = useState([]);
  const [upcomingItems, setUpcomingItems] = useState(null);
  const [showUpcomingModal, setShowUpcomingModal] = useState(false);
  const [showSupervisorApprovalsModal, setShowSupervisorApprovalsModal] = useState(false);
  const [showAllDeliverables, setShowAllDeliverables] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch stats based on role
      if (hasRole(['super_admin', 'admin'])) {
        try {
          const statsRes = await adminAPI.getDashboard();
          setStats(statsRes.data);
        } catch (e) {
          console.error('Failed to fetch admin stats:', e);
        }
      }

      // Fetch recent profiles
      try {
        const profilesRes = await profilesAPI.getAll({ limit: 5 });
        setRecentProfiles(profilesRes.data?.profiles || profilesRes.data || []);
      } catch (e) {
        console.error('Failed to fetch profiles:', e);
      }

      // Fetch today's tasks (skip for coach/supervisor)
      if (!isCoachOrSupervisor) {
        try {
          const tasksRes = await tasksAPI.getAll({ status: 'pending', limit: 5 });
          setTodaysTasks(tasksRes.data?.tasks || tasksRes.data || []);
        } catch (e) {
          console.error('Failed to fetch tasks:', e);
        }
      }

      // Fetch upcoming items for coach modal
      if (isCoach) {
        try {
          const upRes = await coachKpiAPI.getUpcomingItems();
          const items = upRes.data || {};
          setUpcomingItems(items);
          const dismissed = sessionStorage.getItem('upcomingItemsDismissed');
          if (!dismissed && (items.total || 0) > 0) {
            setShowUpcomingModal(true);
          }
        } catch (e) {
          console.error('Failed to fetch upcoming items:', e);
        }
      }

      // Daily-tasks reminder modal is intentionally disabled per backend
      // cleanup — internal matchmakers no longer get the auto-popup on
      // dashboard load. The /tasks page remains the canonical place to
      // see overdue / urgent items. To re-enable, restore the block below.
      // if (isInternalMatchmaker && !isCoach && !isSupervisorCoach) { ... }

      // Fetch recent matches (skip for coach/supervisor)
      if (!isCoachOrSupervisor) {
        try {
          const matchesRes = await matchesAPI.getAll({ limit: 5 });
          setRecentMatches(matchesRes.data?.matches || matchesRes.data || []);
        } catch (e) {
          console.error('Failed to fetch matches:', e);
        }
      }

      // Fetch affiliate stats (skip for coach/supervisor)
      if (!isCoachOrSupervisor) {
        try {
          const [affiliateRes, tiersRes, teamRes] = await Promise.all([
            affiliateAPI.getAccount(),
            affiliateAPI.getTiers(),
            affiliateAPI.getTeam().catch(() => ({ data: {} }))
          ]);
          // Always set stats even if empty - use account data or empty defaults
          const accountData = affiliateRes.data.account || affiliateRes.data || {};
          const teamPayload = teamRes.data || {};
          const teamTotalPoints = teamPayload.stats?.totalTeamPoints
            ?? teamPayload.totalTeamPoints
            ?? 0;
          setAffiliateStats({
            ...accountData,
            tier_name: accountData.tier_name || 'Starter',
            commission_rate: accountData.commission_rate || 5,
            total_commission: accountData.total_commission || 0,
            points_balance: accountData.points_balance || accountData.total_points || 0,
            team_total_points: teamTotalPoints,
            stats: accountData.stats || { total_referrals: 0 }
          });
          setAffiliateTiers(tiersRes.data.tiers || []);
        } catch (e) {
          console.error('Failed to fetch affiliate data:', e);
          // Set default affiliate stats on error so section still shows
          setAffiliateStats({
            tier_name: 'Starter',
            commission_rate: 5,
            total_commission: 0,
            points_balance: 0,
            team_total_points: 0,
            stats: { total_referrals: 0 }
          });
        }
      }

      // Fetch incomplete KPIv2 tickets (coach / leader_coach / supervisor).
      // The "未完成交付项" card is now sourced from KPIv2 (legacy KPI removed):
      // tickets assigned to the current user that aren't closed yet.
      if (showKpiV2Incomplete) {
        try {
          const tRes = await kpiV2API.listTickets({ assigned_user_id: user.id, limit: 200 });
          const tickets = (tRes.data?.data || []).filter(tk => tk.status !== 'closed');
          setIncompleteDeliverables(tickets);
        } catch (e) {
          console.error('Failed to fetch KPIv2 incomplete tickets:', e);
        }
      }

      // Fetch KPI pending approvals (supervisor_coach only)
      if (isSupervisorCoach) {
        try {
          const approvalsRes = await supervisorAPI.getPendingApprovals();
          const approvals = approvalsRes.data?.approvals || [];
          setPendingApprovals(approvals);
          const dismissed = sessionStorage.getItem('supervisorApprovalsDismissed');
          if (!dismissed && approvals.length > 0) {
            setShowSupervisorApprovalsModal(true);
          }
        } catch (e) {
          console.error('Failed to fetch pending approvals:', e);
        }
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getProfileTypeBadge = (type) => {
    const types = {
      lead: 'badge-lead',
      client: 'badge-client',
      member: 'badge-member',
    };
    return types[type] || 'badge-lead';
  };

  // "3 小时前" / "3h ago"
  const relTime = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    const hrs = Math.floor(mins / 60);
    const days = Math.floor(hrs / 24);
    if (isZh) {
      if (days > 0) return `${days}天前`;
      if (hrs > 0) return `${hrs}小时前`;
      if (mins > 0) return `${mins}分钟前`;
      return '刚刚';
    }
    if (days > 0) return `${days}d ago`;
    if (hrs > 0) return `${hrs}h ago`;
    if (mins > 0) return `${mins}m ago`;
    return 'Just now';
  };

  // ── What each role sees (same visibility rules as before) ──
  const firstName = user?.name?.split(' ')[0] || user?.first_name;
  const showTasksBlock = !isCoachOrSupervisor && !isLeaderCoach;
  const showAffiliateBlock = !isCoachOrSupervisor && !isLeaderCoach && !isExternalMatchmaker;

  // Key numbers: admins get the global stats; everyone else gets numbers
  // about their own work. Never more than 5.
  const growthHint = (g) => (g === undefined || g === null)
    ? undefined
    : `${g >= 0 ? '+' : ''}${g}% ${t('dashboard.thisMonth')}`;
  const keyNumbers = [];
  if (stats) {
    keyNumbers.push(
      { label: t('dashboard.totalProfiles'), value: stats.totalProfiles || 0, icon: Users, hint: growthHint(stats.profilesGrowth) },
      { label: t('dashboard.successfulMatches'), value: stats.totalMatches || 0, icon: Heart, tone: 'brand', hint: growthHint(stats.matchesGrowth) },
      { label: t('dashboard.pendingTasks'), value: stats.pendingTasks || 0, icon: CheckSquare, tone: (stats.pendingTasks || 0) > 0 ? 'warn' : undefined },
      { label: t('dashboard.upcomingEvents'), value: stats.upcomingEvents || 0, icon: Calendar },
    );
  } else {
    if (showTasksBlock) {
      keyNumbers.push({
        label: isZh ? '待办任务' : 'Open tasks',
        value: todaysTasks.length >= 5 ? '5+' : todaysTasks.length,
        icon: CheckSquare,
        tone: todaysTasks.length > 0 ? 'warn' : 'ok',
        hint: isZh ? '还没完成' : 'Not done yet',
      });
    }
    if (showKpiV2Incomplete) {
      const overdue = incompleteDeliverables.filter(tk => tk.sla_resolve_at && new Date(tk.sla_resolve_at) < new Date()).length;
      keyNumbers.push({
        label: isZh ? '未完成交付项' : 'Open deliverables',
        value: incompleteDeliverables.length,
        icon: Target,
        tone: overdue > 0 ? 'bad' : undefined,
        hint: overdue > 0 ? (isZh ? `其中 ${overdue} 项已超时` : `${overdue} overdue`) : (isZh ? '没有超时' : 'None overdue'),
      });
    }
    if (isSupervisorCoach) {
      keyNumbers.push({
        label: isZh ? '待审批' : 'Awaiting review',
        value: pendingApprovals.length,
        icon: BarChart3,
        tone: pendingApprovals.length > 0 ? 'bad' : 'ok',
        hint: isZh ? 'KPI 阶段 / 交付项' : 'KPI stages / deliverables',
      });
    }
    if (showAffiliateBlock && affiliateStats) {
      keyNumbers.push(
        { label: t('dashboard.referrals'), value: affiliateStats?.stats?.total_referrals || 0, icon: Gift, hint: isZh ? '推荐计划' : 'Affiliate' },
        { label: t('dashboard.totalEarned'), value: `$${parseFloat(affiliateStats?.total_commission || 0).toFixed(2)}`, icon: Award, tone: 'ok', hint: isZh ? '累计佣金' : 'Total commission' },
      );
    }
  }
  keyNumbers.splice(5);

  // Shortcuts — only links this role already had on the page, plus the
  // general inbox pages every staff member can open.
  const quickActions = [
    !isCoachOrSupervisor && { to: '/profiles/new', icon: Users, label: t('profiles.addProfile'), hint: isZh ? '录入一位新会员' : 'Add a new member' },
    !isCoachOrSupervisor && (canModify || canCreateMatches) && { to: '/matches?action=create', icon: Sparkles, label: t('dashboard.createMatch'), hint: isZh ? '为两位会员牵线' : 'Pair two members' },
    { to: '/profiles', icon: Users, label: isZh ? '全部档案' : 'All profiles', hint: isZh ? '查找、查看会员' : 'Find and view members' },
    !isCoachOrSupervisor && { to: '/matches', icon: Heart, label: isZh ? '全部匹配' : 'All matches', hint: isZh ? '查看匹配进展' : 'Track match progress' },
    showTasksBlock && { to: '/tasks', icon: ListTodo, label: isZh ? '任务' : 'Tasks', hint: isZh ? '查看和完成任务' : 'View and finish tasks' },
    showKpiV2Incomplete && { to: '/kpi-v2', icon: Target, label: isZh ? '交付项' : 'Deliverables', hint: isZh ? '处理 KPI 工单' : 'Work on KPI tickets' },
    // 服务监督 replaces the old 「KPI 审批」 shortcut for the 主管 roles.
    isSupervisorUser(user) && { to: '/service-supervision', icon: ShieldCheck, label: isZh ? '服务监督' : 'Supervision', hint: isZh ? '老师的服务记录和 KPI' : 'Coaches’ records and KPIs' },
    !isCoachOrSupervisor && { to: '/messages', icon: MessageSquare, label: isZh ? '消息' : 'Messages', hint: isZh ? '查看新消息' : 'Read new messages' },
    { to: '/notifications', icon: Bell, label: isZh ? '通知' : 'Notifications', hint: isZh ? '系统提醒' : 'System alerts' },
    showAffiliateBlock && { to: '/affiliate', icon: Gift, label: isZh ? '推荐计划' : 'Affiliate', hint: isZh ? '推荐赚佣金' : 'Refer and earn' },
  ].filter(Boolean);

  if (loading) {
    return (
      <div className="flex justify-center items-center" style={{ minHeight: 400 }}>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // 红娘 (coach) gets a purpose-built 售后服务 overview instead of the generic
  // dashboard: 客户 / 联系 / 跟进 / 约见 / 匹配 / 客户反馈 totals, plus today's
  // follow-up bar. Every other role keeps the page below.
  // Super coaches (leader + supervisor coach) and managers get the same desk
  // with team-wide figures.
  if (isCoach || isLeaderCoach || isSupervisorCoach || isManagerRole) {
    return <CoachOverview />;
  }

  return (
    <div className="dashboard">
      {/* Daily Tasks Notice Modal */}
      {showDailyTasksModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: 'var(--space-lg)'
        }}>
          <div style={{
            background: S.card,
            borderRadius: S.radius,
            width: '100%',
            maxWidth: 600,
            maxHeight: '80vh',
            overflow: 'hidden',
            boxShadow: 'none',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Modal Header */}
            <div style={{
              background: S.ink,
              padding: 'var(--space-xl) var(--space-2xl)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              color: 'white'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
                <div style={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: 'rgba(255,253,249,.10)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Bell size={24} />
                </div>
                <div>
                  <h2 style={{ margin: 0, fontSize: '1.25rem', color: S.card, fontFamily: S.serif, fontWeight: 500, fontSize: '1.6rem' }}>
                    {t('dashboard.dailyTasksReminder')}
                  </h2>
                  <p style={{ margin: 0, fontSize: '0.85rem', opacity: 0.85, marginTop: 4 }}>
                    {dailyTasks.length > 0
                      ? t('dashboard.tasksNeedAttention')
                      : t('dashboard.noTasksScheduled')}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowDailyTasksModal(false);
                  sessionStorage.setItem('dailyTasksDismissed', 'true');
                }}
                style={{
                  background: 'rgba(255,253,249,.10)',
                  border: 'none',
                  borderRadius: '50%',
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: 'white'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{
              padding: 'var(--space-xl) var(--space-2xl)',
              overflowY: 'auto',
              flex: 1
            }}>
              {dailyTasks.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 'var(--space-2xl) 0' }}>
                  <CheckSquare size={56} style={{ color: 'var(--color-success)', marginBottom: 'var(--space-md)' }} />
                  <h3 style={{ margin: 0, marginBottom: 'var(--space-sm)', color: 'var(--color-charcoal)' }}>
                    {t('dashboard.noTasksScheduled')}
                  </h3>
                  <p style={{ color: 'var(--color-slate)', margin: 0, maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
                    {t('dashboard.addTaskSuggestion')}
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
                  {dailyTasks.map(task => {
                    const isOverdue = task.due_date && new Date(task.due_date) < new Date(new Date().toDateString());
                    const isDueToday = task.due_date && new Date(task.due_date).toDateString() === new Date().toDateString();
                    const priorityColors = {
                      urgent: { bg: 'rgba(158,43,37,.09)', color: S.bad, border: 'rgba(158,43,37,.25)' },
                      high: { bg: 'rgba(147,89,26,.10)', color: S.warn, border: 'rgba(147,89,26,.25)' },
                      medium: { bg: 'rgba(138,109,59,.12)', color: S.gold, border: 'rgba(138,109,59,.25)' },
                      low: { bg: 'rgba(63,125,90,.12)', color: S.ok, border: 'rgba(63,125,90,.25)' }
                    };
                    const pc = priorityColors[task.priority] || priorityColors.medium;
                    return (
                      <div
                        key={task.id}
                        style={{
                          padding: 'var(--space-md)',
                          borderRadius: S.radius,
                          border: isOverdue ? `1px solid ${S.bad}` : '1px solid var(--color-border)',
                          background: isOverdue ? 'rgba(158,43,37,.05)' : 'var(--color-cream)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-md)'
                        }}
                      >
                        {isOverdue && (
                          <AlertCircle size={20} style={{ color: S.bad, flexShrink: 0 }} />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {task.title}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginTop: 4 }}>
                            {task.due_date && (
                              <span style={{
                                fontSize: '0.75rem',
                                color: isOverdue ? S.bad : 'var(--color-slate)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4
                              }}>
                                <Clock size={12} />
                                {formatDateLocalized(task.due_date)}
                              </span>
                            )}
                            {isOverdue && (
                              <span style={{
                                fontSize: '0.65rem',
                                padding: '1px 6px',
                                borderRadius: S.radius,
                                background: 'rgba(158,43,37,.09)',
                                color: S.bad,
                                fontWeight: 700
                              }}>
                                {t('dashboard.overdue')}
                              </span>
                            )}
                            {isDueToday && !isOverdue && (
                              <span style={{
                                fontSize: '0.65rem',
                                padding: '1px 6px',
                                borderRadius: S.radius,
                                background: S.soft,
                                color: S.ink2,
                                fontWeight: 700
                              }}>
                                {t('dashboard.dueToday')}
                              </span>
                            )}
                          </div>
                        </div>
                        <span style={{
                          fontSize: '0.65rem',
                          padding: '3px 8px',
                          borderRadius: S.radius,
                          background: pc.bg,
                          color: pc.color,
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          textTransform: 'capitalize'
                        }}>
                          {task.priority}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: 'var(--space-lg) var(--space-2xl)',
              borderTop: '1px solid var(--color-border)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 'var(--space-md)'
            }}>
              <button
                onClick={() => {
                  setShowDailyTasksModal(false);
                  sessionStorage.setItem('dailyTasksDismissed', 'true');
                  navigate('/tasks');
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-sm)',
                  padding: 'var(--space-sm) var(--space-lg)',
                  background: S.ink,
                  color: S.paper,
                  border: 'none',
                  borderRadius: S.radius,
                  fontWeight: 400,
                  letterSpacing: '.18em',
                  cursor: 'pointer',
                  fontSize: '0.8rem'
                }}
              >
                <Plus size={18} />
                {t('dashboard.addNewTask')}
              </button>
              <button
                onClick={() => {
                  setShowDailyTasksModal(false);
                  sessionStorage.setItem('dailyTasksDismissed', 'true');
                }}
                style={{
                  padding: 'var(--space-sm) var(--space-xl)',
                  background: 'transparent',
                  color: S.ink,
                  border: `1px solid ${S.ink}`,
                  borderRadius: S.radius,
                  fontWeight: 400,
                  letterSpacing: '.18em',
                  cursor: 'pointer',
                  fontSize: '0.8rem'
                }}
              >
                {t('dashboard.gotIt')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upcoming Items Modal (for coaches) */}
      {showUpcomingModal && upcomingItems && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 9999, padding: 'var(--space-lg)'
        }}>
          <div style={{
            background: S.card, borderRadius: S.radius, width: '100%', maxWidth: 650,
            maxHeight: '80vh', overflow: 'hidden', boxShadow: 'none',
            display: 'flex', flexDirection: 'column'
          }}>
            <div style={{
              background: S.ink,
              padding: 'var(--space-xl) var(--space-2xl)', display: 'flex',
              alignItems: 'center', justifyContent: 'space-between', color: 'white'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
                <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(255,253,249,.10)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Bell size={24} />
                </div>
                <div>
                  <h2 style={{ margin: 0, fontSize: '1.25rem', color: S.card, fontFamily: S.serif, fontWeight: 500, fontSize: '1.6rem' }}>
                    {t('dashboard.upcomingReminder', 'Upcoming Schedule')}
                  </h2>
                  <p style={{ margin: 0, fontSize: '0.85rem', opacity: 0.85, marginTop: 4 }}>
                    {upcomingItems.total > 0
                      ? `${upcomingItems.total} ${t('dashboard.itemsUpcoming', 'items coming up')}`
                      : t('dashboard.noUpcomingItems', 'No upcoming items')}
                  </p>
                </div>
              </div>
              <button onClick={() => { setShowUpcomingModal(false); sessionStorage.setItem('upcomingItemsDismissed', 'true'); }}
                style={{ background: 'rgba(255,253,249,.10)', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white' }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: 'var(--space-xl) var(--space-2xl)', overflowY: 'auto', flex: 1 }}>
              {upcomingItems.total === 0 ? (
                <div style={{ textAlign: 'center', padding: 'var(--space-2xl) 0' }}>
                  <CheckSquare size={56} style={{ color: 'var(--color-success)', marginBottom: 'var(--space-md)' }} />
                  <h3 style={{ margin: 0, color: 'var(--color-charcoal)' }}>{t('dashboard.noUpcomingItems', 'No upcoming items!')}</h3>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                  {/* Appointments */}
                  {(upcomingItems.appointments || []).length > 0 && (
                    <div>
                      <h4 style={{ margin: '0 0 8px', fontSize: '0.85rem', color: 'var(--color-burgundy)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Calendar size={14} /> {t('coachKpi.tabAppointments', 'Appointments')} ({upcomingItems.appointments.length})
                      </h4>
                      {upcomingItems.appointments.map(a => (
                        <div key={`apt-${a.id}`} style={{ padding: '10px 14px', borderRadius: S.radius, border: `1px solid ${S.lineSoft}`, background: S.paper, marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{a.client_name || 'Appointment'}</div>
                            <div style={{ fontSize: '0.75rem', color: S.sub, marginTop: 2 }}>
                              <Clock size={11} style={{ marginRight: 4, verticalAlign: 'text-bottom' }} />
                              {/* a wall clock in its own timezone (Pacific by default) → instant, shown in Pacific */}
                              {(zonedToInstant(a.appointment_datetime, a.timezone) || new Date(a.appointment_datetime)).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </div>
                          <span style={{ fontSize: '0.65rem', padding: '2px 8px', borderRadius: S.radius, background: S.soft, color: S.ink2, fontWeight: 700 }}>
                            {toPacificWallClock((zonedToInstant(a.appointment_datetime, a.timezone) || new Date(a.appointment_datetime))).toDateString() === toPacificWallClock(new Date()).toDateString() ? (t('common.today', 'Today')) : (t('common.tomorrow', 'Tomorrow'))}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Reminders */}
                  {(upcomingItems.reminders || []).length > 0 && (
                    <div>
                      <h4 style={{ margin: '0 0 8px', fontSize: '0.85rem', color: 'var(--color-burgundy)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Bell size={14} /> {t('coachKpi.tabReminders', 'Reminders')} ({upcomingItems.reminders.length})
                      </h4>
                      {upcomingItems.reminders.map(r => (
                        <div key={`rem-${r.id}`} style={{ padding: '10px 14px', borderRadius: S.radius, border: `1px solid ${S.lineSoft}`, background: S.paper, marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{r.title || r.description || 'Reminder'}</div>
                            <div style={{ fontSize: '0.75rem', color: S.sub, marginTop: 2 }}>
                              <Clock size={11} style={{ marginRight: 4, verticalAlign: 'text-bottom' }} />
                              {r.trigger_date ? new Date(r.trigger_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''} {r.trigger_time || ''}
                            </div>
                          </div>
                          <span style={{ fontSize: '0.65rem', padding: '2px 8px', borderRadius: S.radius, background: 'rgba(138,109,59,.12)', color: S.gold, fontWeight: 700, textTransform: 'capitalize' }}>
                            {r.type || 'reminder'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Dating Schedules */}
                  {(upcomingItems.schedules || []).length > 0 && (
                    <div>
                      <h4 style={{ margin: '0 0 8px', fontSize: '0.85rem', color: 'var(--color-burgundy)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Heart size={14} /> {t('coachKpi.tabSchedules', 'Dating Schedules')} ({upcomingItems.schedules.length})
                      </h4>
                      {upcomingItems.schedules.map(s => (
                        <div key={`sch-${s.id}`} style={{ padding: '10px 14px', borderRadius: S.radius, border: `1px solid ${S.lineSoft}`, background: S.paper, marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{s.client1_name} & {s.client2_name}</div>
                            <div style={{ fontSize: '0.75rem', color: S.sub, marginTop: 2 }}>
                              <Clock size={11} style={{ marginRight: 4, verticalAlign: 'text-bottom' }} />
                              {s.schedule_date ? new Date(s.schedule_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''} {s.schedule_time || ''}
                              {s.location ? ` • ${s.location}` : ''}
                            </div>
                          </div>
                          <span style={{ fontSize: '0.65rem', padding: '2px 8px', borderRadius: S.radius, background: 'rgba(147,89,26,.10)', color: S.warn, fontWeight: 700, textTransform: 'capitalize' }}>
                            {s.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Follow-ups */}
                  {(upcomingItems.followups || []).length > 0 && (
                    <div>
                      <h4 style={{ margin: '0 0 8px', fontSize: '0.85rem', color: 'var(--color-burgundy)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Target size={14} /> {t('coachKpi.tabFollowups', 'Follow-ups')} ({upcomingItems.followups.length})
                      </h4>
                      {upcomingItems.followups.map(f => (
                        <div key={`fu-${f.id}`} style={{ padding: '10px 14px', borderRadius: S.radius, border: `1px solid ${S.lineSoft}`, background: S.paper, marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{f.client_name || 'Follow-up'}</div>
                            <div style={{ fontSize: '0.75rem', color: S.sub, marginTop: 2 }}>
                              {t('coachKpi.attempt', 'Attempt')} {f.attempt_count || 1}/{f.max_attempts || 3}
                              {f.notes ? ` • ${f.notes.substring(0, 50)}` : ''}
                            </div>
                          </div>
                          <span style={{ fontSize: '0.65rem', padding: '2px 8px', borderRadius: S.radius, background: 'rgba(147,89,26,.10)', color: S.warn, fontWeight: 700 }}>
                            {t('common.pending', 'Pending')}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{
              padding: 'var(--space-lg) var(--space-2xl)', borderTop: '1px solid var(--color-border)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-md)'
            }}>
              <button onClick={() => { setShowUpcomingModal(false); sessionStorage.setItem('upcomingItemsDismissed', 'true'); navigate('/kpi'); }}
                style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', padding: 'var(--space-sm) var(--space-lg)', background: S.ink, color: S.paper, border: 'none', borderRadius: S.radius, fontWeight: 400, letterSpacing: '.18em', cursor: 'pointer', fontSize: '0.8rem' }}>
                <Eye size={18} /> {t('dashboard.viewKPI', 'View KPI')}
              </button>
              <button onClick={() => { setShowUpcomingModal(false); sessionStorage.setItem('upcomingItemsDismissed', 'true'); }}
                style={{ padding: 'var(--space-sm) var(--space-xl)', background: 'transparent', color: S.ink, border: `1px solid ${S.ink}`, borderRadius: S.radius, fontWeight: 400, letterSpacing: '.18em', cursor: 'pointer', fontSize: '0.8rem' }}>
                {t('dashboard.gotIt', 'Got it')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Supervisor Upcoming Approvals Modal */}
      {showSupervisorApprovalsModal && pendingApprovals.length > 0 && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 9999, padding: 'var(--space-lg)'
        }}>
          <div style={{
            background: S.card, borderRadius: S.radius, width: '100%', maxWidth: 650,
            maxHeight: '80vh', overflow: 'hidden', boxShadow: 'none',
            display: 'flex', flexDirection: 'column'
          }}>
            <div style={{
              background: S.ink,
              padding: 'var(--space-xl) var(--space-2xl)', display: 'flex',
              alignItems: 'center', justifyContent: 'space-between', color: 'white'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
                <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(255,253,249,.10)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <AlertCircle size={24} />
                </div>
                <div>
                  <h2 style={{ margin: 0, fontSize: '1.25rem', color: S.card, fontFamily: S.serif, fontWeight: 500, fontSize: '1.6rem' }}>
                    {t('dashboard.pendingApprovals', 'Pending Approvals')}
                  </h2>
                  <p style={{ margin: 0, fontSize: '0.85rem', opacity: 0.85, marginTop: 4 }}>
                    {pendingApprovals.length} {t('dashboard.stagesAwaitingReview', 'stages/deliverables awaiting review')}
                  </p>
                </div>
              </div>
              <button onClick={() => { setShowSupervisorApprovalsModal(false); sessionStorage.setItem('supervisorApprovalsDismissed', 'true'); }}
                style={{ background: 'rgba(255,253,249,.10)', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white' }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: 'var(--space-xl) var(--space-2xl)', overflowY: 'auto', flex: 1 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
                {pendingApprovals.slice(0, 10).map(approval => {
                  const timeAgo = (() => {
                    if (!approval.requested_at) return '';
                    const diff = Date.now() - new Date(approval.requested_at).getTime();
                    const mins = Math.floor(diff / 60000);
                    const hrs = Math.floor(mins / 60);
                    const days = Math.floor(hrs / 24);
                    if (isZh) {
                      if (days > 0) return `${days}天前`;
                      if (hrs > 0) return `${hrs}小时前`;
                      if (mins > 0) return `${mins}分钟前`;
                      return '刚刚';
                    }
                    if (days > 0) return `${days}d ago`;
                    if (hrs > 0) return `${hrs}h ago`;
                    if (mins > 0) return `${mins}m ago`;
                    return 'Just now';
                  })();
                  return (
                    <div
                      key={approval.id}
                      onClick={() => {
                        setShowSupervisorApprovalsModal(false);
                        sessionStorage.setItem('supervisorApprovalsDismissed', 'true');
                        navigate(`/supervisor/kpi?assignment=${approval.assignment_id}&stage=${approval.id}`);
                      }}
                      style={{
                        padding: '12px 14px', borderRadius: S.radius,
                        border: `1px solid ${S.lineSoft}`, background: S.paper,
                        display: 'flex', alignItems: 'center', gap: 'var(--space-md)',
                        cursor: 'pointer', transition: 'background 0.15s'
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = S.soft; }}
                      onMouseLeave={e => { e.currentTarget.style.background = S.paper; }}
                    >
                      <div style={{
                        width: 36, height: 36, borderRadius: '50%',
                        background: S.gold,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                      }}>
                        <Send size={16} color={S.card} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                          {approval.stage_name_zh || approval.stage_name}
                          {approval.stage_code && (
                            <span style={{ marginLeft: 6, fontSize: '0.7rem', padding: '1px 6px', borderRadius: S.radius, background: S.soft, color: S.ink2, fontWeight: 700 }}>
                              {approval.stage_code}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.78rem', color: S.sub, marginTop: 2 }}>
                          {approval.coach_first} {approval.coach_last} • {approval.client_name || approval.workflow_name_zh || approval.workflow_name}
                        </div>
                      </div>
                      <span style={{ fontSize: '0.7rem', color: S.sub, display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                        <Clock size={12} /> {timeAgo}
                      </span>
                    </div>
                  );
                })}
                {pendingApprovals.length > 10 && (
                  <p style={{ textAlign: 'center', fontSize: '0.8rem', color: S.sub, margin: '8px 0 0' }}>
                    +{pendingApprovals.length - 10} {t('common.more', 'more')}...
                  </p>
                )}
              </div>
            </div>

            <div style={{
              padding: 'var(--space-lg) var(--space-2xl)', borderTop: '1px solid var(--color-border)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-md)'
            }}>
              <button onClick={() => { setShowSupervisorApprovalsModal(false); sessionStorage.setItem('supervisorApprovalsDismissed', 'true'); navigate('/supervisor/kpi'); }}
                style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', padding: 'var(--space-sm) var(--space-lg)', background: S.ink, color: S.paper, border: 'none', borderRadius: S.radius, fontWeight: 400, letterSpacing: '.18em', cursor: 'pointer', fontSize: '0.8rem' }}>
                <Eye size={18} /> {t('dashboard.reviewApprovals', 'Review Approvals')}
              </button>
              <button onClick={() => { setShowSupervisorApprovalsModal(false); sessionStorage.setItem('supervisorApprovalsDismissed', 'true'); }}
                style={{ padding: 'var(--space-sm) var(--space-xl)', background: 'transparent', color: S.ink, border: `1px solid ${S.ink}`, borderRadius: S.radius, fontWeight: 400, letterSpacing: '.18em', cursor: 'pointer', fontSize: '0.8rem' }}>
                {t('dashboard.gotIt', 'Got it')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Header: greeting + what this page is for + the two main actions ── */}
      <PageHeader
        kicker={`HOME · ${formatPacific(format, new Date(), isZh ? 'M 月 d 日' : 'EEE, MMM d')} · ${isZh ? '美西时间 PT' : 'PT'}`}
        title={isZh ? `你好，${firstName || ''}` : `Hi, ${firstName || ''}`}
        subtitle={isZh
          ? '这是你的工作首页：先处理「今天要做的事」，再看看关键数字和最近的新档案、新匹配。'
          : 'Your home base: handle today\'s to-dos first, then check key numbers and the latest profiles and matches.'}
        actions={!isCoachOrSupervisor && (
          <>
            <Link to="/profiles/new" className="btn btn-primary">
              <Users size={16} />
              {t('profiles.addProfile')}
            </Link>
            {canModify && (
              <Link to="/matches" className="btn btn-secondary">
                <Sparkles size={16} />
                {t('matches.createMatch')}
              </Link>
            )}
          </>
        )}
        helpTitle={isZh ? '这个页面怎么用？' : 'How to use this page'}
        help={isZh ? (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li><b>今天要做的事</b>：需要你处理的事项（任务、交付项、审批），点一下就能进入处理。</li>
            <li><b>关键数字</b>：一眼看清整体情况。</li>
            <li><b>常用入口</b>：一键去到最常用的页面。</li>
            <li><b>最近动态</b>：最新加入的档案和最新的匹配。</li>
          </ul>
        ) : (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li><b>Today's to-dos</b>: things waiting for you (tasks, deliverables, approvals) — click one to handle it.</li>
            <li><b>Key numbers</b>: the overall picture at a glance.</li>
            <li><b>Shortcuts</b>: jump straight to the pages you use most.</li>
            <li><b>Recent activity</b>: the newest profiles and matches.</li>
          </ul>
        )}
      />

      {/* ── Key numbers (shown first, as in the Luxe layout) (max 5) ── */}
      {keyNumbers.length > 0 && (
        <>
          <h2 className="sh-eyebrow">{isZh ? '关键数字' : 'Key numbers'}</h2>
          <StatStrip items={keyNumbers} />
        </>
      )}

      {/* ── 1. Today's to-dos (role-dependent) ── */}
      {(isSupervisorCoach || showKpiV2Incomplete || showTasksBlock) && (
        <>
          <h2 className="sh-eyebrow">{isZh ? '今天要做的事' : "Today's to-dos"}</h2>
          <div className="sh-grid">
            {/* KPI approvals — supervisor_coach only */}
            {isSupervisorCoach && (
              <Section
                title={<span className="sh-title"><BarChart3 size={17} style={{ color: S.brand }} />{t('dashboard.pendingKpiApprovals')}{pendingApprovals.length > 0 && <Pill tone="bad">{pendingApprovals.length}</Pill>}</span>}
                description={isZh ? '红娘提交的阶段 / 交付项，等你审核。' : 'Stages and deliverables submitted by coaches, waiting for your review.'}
                actions={<Link to="/supervisor/kpi" className="btn btn-sm btn-secondary">{t('common.viewAll')}<ArrowRight size={14} /></Link>}
                bodyStyle={{ padding: '4px 18px' }}
              >
                {pendingApprovals.length === 0 ? (
                  <EmptyState icon={CheckSquare} title={t('dashboard.noApprovalsPending')} description={t('dashboard.allApprovalsUpToDate')} />
                ) : (
                  pendingApprovals.map((approval, i) => (
                    <div key={approval.id} className="sh-row" style={i === 0 ? { borderTop: 0 } : undefined}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="sh-name">
                          {approval.stage_name_zh || approval.stage_name}
                          {approval.stage_code && <span style={{ marginLeft: 6 }}><Pill tone="brand">{approval.stage_code}</Pill></span>}
                        </div>
                        <div className="sh-sub">
                          {approval.coach_first} {approval.coach_last} · {approval.client_name || `${approval.workflow_name_zh || approval.workflow_name}`}
                          {approval.requested_at && <> · <Clock size={11} style={{ verticalAlign: '-1px' }} /> {relTime(approval.requested_at)}</>}
                        </div>
                      </div>
                      <Link
                        to={`/supervisor/kpi?assignment=${approval.assignment_id}&stage=${approval.id}`}
                        className="btn btn-sm btn-primary"
                        style={{ flexShrink: 0 }}
                      >
                        <Eye size={13} /> {t('dashboard.review')}
                      </Link>
                    </div>
                  ))
                )}
              </Section>
            )}

            {/* Incomplete KPIv2 tickets (coach / leader_coach / supervisor) */}
            {showKpiV2Incomplete && (
              <Section
                title={<span className="sh-title"><Target size={17} style={{ color: S.brand }} />{t('dashboard.incompleteDeliverables')}{incompleteDeliverables.length > 0 && <Pill tone="warn">{incompleteDeliverables.length}</Pill>}</span>}
                description={isZh ? '分配给你、还没关闭的工单。标「已超时」的请优先处理。' : 'Tickets assigned to you that are still open. Handle the overdue ones first.'}
                actions={<Link to="/kpi-v2" className="btn btn-sm btn-secondary">{t('common.viewAll')}<ArrowRight size={14} /></Link>}
                bodyStyle={{ padding: '4px 18px' }}
              >
                {incompleteDeliverables.length === 0 ? (
                  <EmptyState icon={CheckSquare} title={t('dashboard.allDeliverablesComplete')} description={t('dashboard.noIncompleteDeliverables')} />
                ) : (
                  <>
                    {(showAllDeliverables ? incompleteDeliverables : incompleteDeliverables.slice(0, DELIVERABLES_PREVIEW)).map((tk, i) => {
                      // KPIv2 ticket status → label + tone.
                      const STATUS_META = {
                        open: { zh: '待处理', en: 'Open', tone: 'warn' },
                        in_progress: { zh: '处理中', en: 'In progress', tone: 'brand' },
                        pending_followup: { zh: '待跟进', en: 'Pending follow-up', tone: 'neutral' },
                      };
                      const meta = STATUS_META[tk.status] || { zh: tk.status, en: tk.status, tone: 'neutral' };
                      const awaitingReview = !!tk.pending_status_change;
                      const isOverdue = tk.sla_resolve_at && new Date(tk.sla_resolve_at) < new Date();
                      return (
                        <div
                          key={tk.id}
                          className="sh-row sh-click"
                          onClick={() => navigate('/kpi-v2')}
                          style={i === 0 ? { borderTop: 0 } : undefined}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="sh-name sh-ellipsis" style={isOverdue ? { color: S.bad } : undefined}>
                              {isOverdue && <AlertCircle size={14} style={{ verticalAlign: '-2px', marginRight: 4 }} />}
                              {tk.title || tk.ticket_no}
                            </div>
                            <div className="sh-sub">
                              {[tk.client_name, tk.ticket_no].filter(Boolean).join(' · ')}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end', flexShrink: 0 }}>
                            {isOverdue && <Pill tone="bad">{isZh ? '已超时' : 'Overdue'}</Pill>}
                            {awaitingReview && <Pill tone="warn">{isZh ? '待审核' : 'In review'}</Pill>}
                            <Pill tone={meta.tone}>{isZh ? meta.zh : meta.en}</Pill>
                          </div>
                          <ArrowRight size={15} style={{ color: S.mute, flexShrink: 0 }} />
                        </div>
                      );
                    })}
                    {incompleteDeliverables.length > DELIVERABLES_PREVIEW && (
                      <button type="button" className="sh-more" onClick={() => setShowAllDeliverables((v) => !v)}>
                        {showAllDeliverables
                          ? (isZh ? '收起' : 'Show less')
                          : (isZh ? `显示全部 ${incompleteDeliverables.length} 项` : `Show all ${incompleteDeliverables.length}`)}
                        <ChevronDown size={14} style={{ transform: showAllDeliverables ? 'rotate(180deg)' : 'none' }} />
                      </button>
                    )}
                  </>
                )}
              </Section>
            )}

            {/* Pending tasks — hidden for coach / supervisor / leader coach */}
            {showTasksBlock && (
              <Section
                title={<span className="sh-title"><CheckSquare size={17} style={{ color: S.brand }} />{t('dashboard.todaysTasks')}</span>}
                description={isZh ? '还没完成的任务。完成后到「任务」页面勾掉即可。' : 'Tasks not done yet. Tick them off on the Tasks page when finished.'}
                actions={<Link to="/tasks" className="btn btn-sm btn-secondary">{t('common.viewAll')}<ArrowRight size={14} /></Link>}
                bodyStyle={{ padding: '4px 18px' }}
              >
                {todaysTasks.length === 0 ? (
                  <EmptyState icon={CheckSquare} title={t('tasks.allCaughtUp')} description={t('tasks.noPendingToday')} />
                ) : (
                  todaysTasks.map((task, i) => (
                    <div key={task.id} className="sh-row" style={i === 0 ? { borderTop: 0 } : undefined}>
                      <span style={{ width: 16, height: 16, borderRadius: 0, border: `1px solid ${S.ink}`, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="sh-name">{task.title}</div>
                        <div className="sh-sub">
                          <Clock size={11} style={{ verticalAlign: '-1px', marginRight: 4 }} />
                          {task.due_date
                            ? formatPacific(format, task.due_date, 'h:mm a')
                            : t('tasks.noDueTime')}
                        </div>
                      </div>
                      {task.priority === 'high' && <Pill tone="bad">{t('tasks.high')}</Pill>}
                    </div>
                  ))
                )}
              </Section>
            )}
          </div>
        </>
      )}

      {/* ── 3. Shortcuts ── */}
      <h2 className="sh-eyebrow">{isZh ? '常用入口' : 'Shortcuts'}</h2>
      <div className="sh-quick">
        {quickActions.map((qa) => {
          const Icon = qa.icon;
          return (
            <Link key={qa.to} to={qa.to} className="sh-quick-item">
              <span className="sh-quick-icon"><Icon size={17} /></span>
              <span style={{ minWidth: 0 }}>
                <span className="sh-quick-label">{qa.label}</span>
                <span className="sh-quick-hint">{qa.hint}</span>
              </span>
            </Link>
          );
        })}
      </div>

      {/* ── 4. Recent activity ── */}
      <h2 className="sh-eyebrow">{isZh ? '最近动态' : 'Recent activity'}</h2>
      <div className="sh-grid">
        {/* Recent profiles */}
        <Section
          title={t('dashboard.recentProfiles')}
          actions={<Link to="/profiles" className="btn btn-sm btn-secondary">{t('common.viewAll')}<ArrowRight size={14} /></Link>}
          bodyStyle={{ padding: '4px 18px' }}
        >
          {recentProfiles.length === 0 ? (
            <EmptyState
              icon={Users}
              title={t('profiles.noProfiles')}
              description={t('profiles.startByAdding')}
              action={<Link to="/profiles/new" className="btn btn-primary">{t('profiles.addProfile')}</Link>}
            />
          ) : (
            recentProfiles.map((profile, i) => (
              <div key={profile.id} className="sh-row" style={i === 0 ? { borderTop: 0 } : undefined}>
                <div className="sh-avatar">
                  {getInitials(`${profile.first_name} ${profile.last_name}`)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Link to={`/profiles/${profile.id}`} className="sh-name sh-serif sh-ellipsis" style={{ display: 'block', color: S.ink }}>
                    {profile.first_name} {profile.last_name}
                  </Link>
                  <div className="sh-sub sh-ellipsis">
                    {[profile.email, formatDateLocalized(profile.created_at)].filter(Boolean).join(' · ')}
                  </div>
                </div>
                {profile.profile_type && (
                  <span className={`badge ${getProfileTypeBadge(profile.profile_type)}`} style={{ flexShrink: 0 }}>
                    {profileTypeLabel(profile.profile_type)}
                  </span>
                )}
              </div>
            ))
          )}
        </Section>

        {/* Recent matches — hidden for coach/supervisor */}
        {!isCoachOrSupervisor && (
          <Section
            title={t('dashboard.recentMatches')}
            actions={
              <>
                {(canModify || canCreateMatches) && (
                  <Link to="/matches?action=create" className="btn btn-sm btn-primary">
                    <Sparkles size={14} />
                    {t('dashboard.createMatch')}
                  </Link>
                )}
                <Link to="/matches" className="btn btn-sm btn-secondary">{t('common.viewAll')}<ArrowRight size={14} /></Link>
              </>
            }
            bodyStyle={{ padding: '4px 18px' }}
          >
            {recentMatches.length === 0 ? (
              <EmptyState
                icon={Heart}
                title={t('matches.noMatches')}
                description={isZh ? '为合适的两位会员创建第一个匹配吧。' : 'Start creating matches between compatible profiles.'}
                action={(canModify || canCreateMatches) && (
                  <Link to="/matches?action=create" className="btn btn-primary">{t('dashboard.createMatch')}</Link>
                )}
              />
            ) : (
              recentMatches.map((match, i) => {
                const name1 = match.profile1_name || `${match.profile1_first_name || ''} ${match.profile1_last_name || ''}`;
                const name2 = match.profile2_name || `${match.profile2_first_name || ''} ${match.profile2_last_name || ''}`;
                return (
                  <div key={match.id} className="sh-row" style={i === 0 ? { borderTop: 0 } : undefined}>
                    <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                      {match.profile1_photo
                        ? <img src={match.profile1_photo} alt="" className="sh-avatar" style={{ objectFit: 'cover' }} />
                        : <div className="sh-avatar">{getInitials(name1)}</div>}
                      {match.profile2_photo
                        ? <img src={match.profile2_photo} alt="" className="sh-avatar" style={{ objectFit: 'cover', marginLeft: -8, border: `2px solid ${S.card}` }} />
                        : <div className="sh-avatar" style={{ marginLeft: -8, border: `2px solid ${S.card}` }}>{getInitials(name2)}</div>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="sh-name sh-serif sh-ellipsis">
                        {(name1.trim() || (isZh ? '未知' : 'Unknown'))} &amp; {(name2.trim() || (isZh ? '未知' : 'Unknown'))}
                      </div>
                      <div className="sh-sub">
                        {t('dashboard.matched')} {formatDateLocalized(match.created_at)}
                        {' · '}
                        <span style={{ color: S.gold, fontWeight: 600 }}>
                          <Star size={11} fill="currentColor" style={{ verticalAlign: '-1px' }} /> {match.match_percentage || 0}%
                        </span>
                      </div>
                    </div>
                    <Pill tone={MATCH_TONE[match.status] || 'warn'}>{matchStatusLabel(match.status)}</Pill>
                  </div>
                );
              })
            )}
          </Section>
        )}
      </div>

      {/* ── 5. Affiliate programme progress — hidden for coach / supervisor /
          leader_coach AND for external matchmakers. ── */}
      {showAffiliateBlock && (
        <Section
          title={<span className="sh-title"><Gift size={17} style={{ color: S.gold }} />{t('affiliate.title')}</span>}
          description={t('affiliate.progressToNext')}
          actions={<Link to="/affiliate" className="btn btn-sm btn-secondary">{t('dashboard.viewDetails')} <ArrowRight size={14} /></Link>}
        >
          {(() => {
            const currentPoints = affiliateStats?.team_total_points || 0;
            const currentTier = affiliateStats?.tier_name || 'Starter';
            const sortedTiers = [...affiliateTiers].sort((a, b) => (a.points_required || 0) - (b.points_required || 0));
            const currentTierIndex = sortedTiers.findIndex(t => t.name === currentTier);
            const nextTier = sortedTiers[currentTierIndex + 1];
            const currentTierData = sortedTiers[currentTierIndex] || { points_required: 0 };
            const nextTierPoints = nextTier?.points_required || (currentTierData.points_required + 100);
            const pointsNeeded = Math.max(0, nextTierPoints - currentPoints);
            const progressPercent = nextTier
              ? Math.min(((currentPoints - currentTierData.points_required) / (nextTierPoints - currentTierData.points_required)) * 100, 100)
              : 100;

            return (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8, fontSize: 13.5 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <Award size={16} style={{ color: S.gold }} />
                    <span style={{ fontWeight: 700, color: S.brand }}>{currentTier}</span>
                    <span style={{ color: S.sub }}>
                      · {currentPoints.toLocaleString()} {t('affiliate.teamTotalPoints', '团队总积分')}
                    </span>
                  </div>
                  {nextTier ? (
                    <span style={{ color: S.sub }}>
                      {pointsNeeded.toLocaleString()} {t('dashboard.ptsTo')} {nextTier.name}
                    </span>
                  ) : (
                    <span style={{ color: S.ok, fontWeight: 600 }}>
                      ✓ {isZh ? '已达到最高等级' : 'Top tier achieved!'}
                    </span>
                  )}
                </div>
                <div style={{ height: 4, background: S.line, borderRadius: 0, overflow: 'hidden' }}>
                  <div style={{
                    width: `${progressPercent}%`,
                    height: '100%',
                    background: S.goldDecor,
                    borderRadius: 0,
                    transition: 'width 0.5s ease'
                  }} />
                </div>
                <div className="sh-mini-stats">
                  <div>
                    <b>{affiliateStats?.stats?.total_referrals || 0}</b>
                    <span>{t('dashboard.referrals')}</span>
                  </div>
                  <div>
                    <b style={{ color: S.ok }}>${parseFloat(affiliateStats?.total_commission || 0).toFixed(2)}</b>
                    <span>{t('dashboard.totalEarned')}</span>
                  </div>
                  <div>
                    <b>{affiliateStats?.commission_rate || 10}%</b>
                    <span>{t('dashboard.commissionRate')}</span>
                  </div>
                </div>
              </>
            );
          })()}
        </Section>
      )}

      <style>{`
        .sh-eyebrow { margin: 8px 0 16px; font-family: ${S.serif}; font-size: 28px; font-weight: 500; line-height: 1.2; color: ${S.ink}; letter-spacing: 0; }
        .sh-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 380px), 1fr)); column-gap: 24px; align-items: start; }
        .sh-title { display: inline-flex; align-items: center; gap: 8px; flex-wrap: wrap; }
        .sh-row { display: flex; align-items: center; gap: 14px; padding: 14px 0; border-top: 1px solid ${S.lineSoft}; }
        .sh-click { cursor: pointer; border-radius: 0; }
        .sh-click:hover { background: ${S.soft}; }
        .sh-name { font-size: 14px; font-weight: 400; color: ${S.ink}; line-height: 1.4; }
        .sh-serif { font-family: ${S.serif}; font-size: 19px; font-weight: 500; line-height: 1.25; }
        .sh-sub { margin-top: 2px; font-size: 12px; color: ${S.sub}; line-height: 1.5; }
        .sh-ellipsis { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .sh-avatar { width: 40px; height: 50px; border-radius: 0; flex-shrink: 0; background: ${S.soft}; color: ${S.gold}; display: flex; align-items: center; justify-content: center; font-family: ${S.serif}; font-size: 18px; font-weight: 500; box-sizing: border-box; }
        .sh-more { display: flex; align-items: center; justify-content: center; gap: 4px; width: 100%; padding: 10px 0 12px; background: none; border: 0; border-top: 1px solid ${S.lineSoft}; color: ${S.gold}; font-size: 12px; letter-spacing: .2em; font-weight: 400; cursor: pointer; }
        .sh-quick { display: grid; grid-template-columns: repeat(auto-fill, minmax(min(100%, 180px), 1fr)); gap: 0 20px; margin-bottom: 36px; border-top: 1px solid ${S.ink}; }
        .sh-quick-item { display: flex; align-items: flex-start; gap: 10px; padding: 18px 0; background: transparent; border-bottom: 1px solid ${S.line}; border-radius: 0; text-decoration: none; color: ${S.ink}; transition: color .15s; min-width: 0; }
        .sh-quick-item:hover { color: ${S.gold}; }
        .sh-quick-icon { width: 20px; height: 26px; flex-shrink: 0; background: transparent; color: ${S.goldDecor}; display: flex; align-items: center; justify-content: center; }
        .sh-quick-label { display: block; font-family: ${S.serif}; font-size: 20px; font-weight: 500; line-height: 1.25; }
        .sh-quick-hint { display: block; font-size: 12.5px; color: ${S.sub}; margin-top: 4px; }
        .sh-mini-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0; margin-top: 16px; padding-top: 14px; border-top: 1px solid ${S.line}; text-align: center; }
        .sh-mini-stats > div + div { border-left: 1px solid ${S.line}; }
        .sh-mini-stats b { display: block; font-family: ${S.serif}; font-size: 28px; font-weight: 500; line-height: 1.1; color: ${S.ink}; }
        .sh-mini-stats span { display: block; font-size: 11.5px; letter-spacing: .18em; color: ${S.sub}; margin-top: 4px; }
        @media (max-width: 768px) {
          .sh-quick { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .sh-quick-hint { display: none; }
          .sh-eyebrow { font-size: 24px; }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
